import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { applyAccent } from '../lib/accentTheme';
import { API } from '../lib/api';
import { setSignature, setTemplates } from '../lib/waShare';
import { useAuth } from './AuthContext';

const TenantContext = createContext(null);

/**
 * The signed-in workshop's plan and entitlements.
 *
 * Everything here is advisory for the UI only — the backend middleware is what
 * actually blocks a locked account or a module outside the plan. This exists so
 * the interface can explain *why* before the user runs into a wall.
 */
export const TenantProvider = ({ children }) => {
  const { user, authHeader } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [access, setAccess] = useState(null);
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  // Object URL for the workshop's own logo, shown in the app chrome. Fetched
  // as a blob because the endpoint needs the session.
  const [logoUrl, setLogoUrl] = useState('');
  const [accent, setAccent] = useState('');

  const refresh = useCallback(async () => {
    if (!user) { setTenant(null); setAccess(null); setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/tenant/me`, { headers: authHeader() });
      setTenant(data.tenant);
      setAccess(data.access);
      setPlans(data.plans || []);
      setModules(data.modules || []);
      // Remember which workshop signed in on this device so their sign-in
      // screen can greet them with their own logo next time.
      if (data.tenant?.slug) {
        try { window.localStorage.setItem('driveline:workshop', data.tenant.slug); } catch { /* private mode */ }
      }
      setSignature(data.signature);
      setTemplates(data.message_templates);
      setAccent(data.brand_color || '');
      applyAccent(data.brand_color || '');
      // Separate try/catch on purpose: a failed logo fetch must not fall into
      // the outer catch, which resets `access` and would hide the trial banner
      // and plan gating.
      try {
        if (data.has_logo) {
          const res = await axios.get(`${API}/tenant/logo`, { headers: authHeader(), responseType: 'blob' });
          setLogoUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(res.data); });
        } else {
          setLogoUrl((old) => { if (old) URL.revokeObjectURL(old); return ''; });
        }
      } catch (_logoErr) {
        setLogoUrl((old) => { if (old) URL.revokeObjectURL(old); return ''; });
      }
    } catch (_e) {
      // A failure here must never lock the user out of a working app.
      setAccess({ state: 'active', read_only: false, features: [], days_left: 0 });
    } finally {
      setLoading(false);
    }
  }, [user, authHeader]);

  useEffect(() => { refresh(); }, [refresh]);

  // A locked account gets a 402 on every write. Components were written long
  // before subscriptions existed and mostly swallow errors, so without this the
  // user taps Save and nothing visibly happens. One global interceptor turns
  // that into a readable explanation wherever it occurs.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const data = err?.response?.data;
        if (err?.response?.status === 402 && data?.code === 'subscription_required') {
          toast.error(data.detail || 'Your subscription has lapsed', {
            description: 'Viewing and downloads still work. Reactivate to create or edit records.',
            action: { label: 'Reactivate', onClick: () => { window.location.href = '/admin/subscription'; } },
          });
        }
        if (err?.response?.status === 402 && data?.code === 'module_removed') {
          toast.error(data.detail || 'This module is no longer part of your plan', {
            description: 'Your existing records stay available to view and download.',
            action: { label: 'See plans', onClick: () => { window.location.href = '/admin/subscription'; } },
          });
        }
        return Promise.reject(err);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const hasFeature = useCallback((key) => {
    if (!access) return true;            // still loading — don't hide the app
    if (!access.features?.length) return true;
    return access.features.includes(key);
  }, [access]);

  // A module they used to have: the records stay readable, writing is blocked.
  const isViewOnlyFeature = useCallback(
    (key) => Boolean(access?.view_only_features?.includes(key)), [access],
  );

  // What it would take to unlock a module they do not have.
  const moduleInfo = useCallback(
    (key) => (modules || []).find((m) => m.key === key) || null, [modules],
  );

  return (
    <TenantContext.Provider value={{
      tenant, access, plans, modules, loading, refresh, hasFeature, logoUrl, accent,
      isViewOnlyFeature, moduleInfo,
      readOnly: Boolean(access?.read_only),
      isPlatformOwner: Boolean(user?.is_platform_owner),
    }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext) || {
  tenant: null, access: null, plans: [], modules: [], loading: false,
  refresh: () => {}, hasFeature: () => true, readOnly: false, isPlatformOwner: false,
  isViewOnlyFeature: () => false, moduleInfo: () => null,
  logoUrl: '', accent: '',
};
